import type {RepositoryDataResponse} from '@github-ui/data-router-error-helpers'
import type {MilestoneShowResponse} from '@github-ui/dotcom-schema/types/issues/v1/milestone_show'
import {getQueryClient} from '@github-ui/query-client'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery, makeSelectDataFromQueryData} from '@github-ui/react-core/future/main-query'
import {matchPath} from '@github-ui/react-router'
import {RepositoryMilestoneEditPageQuery} from '@github-ui/server-persisted-queries'
import type {RepositoryMilestoneEditPageQueryQuery} from '@github-ui/server-persisted-queries/types/RepositoryMilestoneEditPageQuery'

import {repoAppBuilder} from '@github-ui/repo-app-builder'

import {milestoneHeaderQueryKey, milestoneHeaderToView} from './milestone-show-route'

// need this type to expose the errors array so we can detect FORBIDDEN
export type MilestoneEditPayload = RepositoryDataResponse<{
  repository:
    | (Omit<NonNullable<RepositoryMilestoneEditPageQueryQuery['repository']>, 'viewerCanPush'> & {
        viewerCanPush?: boolean
      })
    | null
}>

const path = '/:owner/:repo/milestones/:number/edit'

export function buildMilestoneEditRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  const query = mainQuery<MilestoneEditPayload>({
    // owner/name/number are resolved from the pathname by the BFF.
    queryDeps: ({pathname}) => ({
      pathname,
      searchParams: {queryId: RepositoryMilestoneEditPageQuery},
    }),
  })
  return appBuilder.createQueryRouteConfig('milestoneEditRoute', {
    path,
    queries: [
      {
        ...query,
        select: makeSelectDataFromQueryData<MilestoneEditPayload>(),
        queryFn: async (queryKey, options) => {
          const params = matchPath(path, queryKey.queryDeps.pathname)?.params
          if (params?.owner && params.repo && params.number) {
            const header = getQueryClient().getQueryData<MilestoneShowResponse>(
              milestoneHeaderQueryKey(params.owner, params.repo, Number.parseInt(params.number, 10)),
            )
            if (header?.repository?.milestone) {
              return {meta: undefined, payload: {data: milestoneHeaderToView(header)}}
            }
          }
          return query.queryFn(queryKey, options)
        },
      },
    ],
  })
}

export const milestoneEditRoute = buildMilestoneEditRoute(repoAppBuilder)

export function milestoneEditQueryKey(owner: string, repo: string, number: number) {
  return milestoneEditRoute.queries.mainQuery.makeQueryKey({
    pathname: `/${owner}/${repo}/milestones/${number}/edit`,
    searchParams: {queryId: RepositoryMilestoneEditPageQuery},
  })
}
